import { OTPInputFieldProps } from "./OTPInputField.types";
import {
  CircleIcon,
  OTPInputFieldBox,
  OTPInputFieldContainer,
  OTPInputFieldsContainer,
  OTPInputFieldWrapper,
  SupportingText,
  Wrapper,
} from "./OTPInputField.styles";
import {
  Body4RegularTypography,
  Caps2SemiboldTypography,
} from "../../primitives/Typography/typographyVariants";
import { FlexColumn } from "../Layout/FlexColumn/FlexColumn";
import { FlexRow } from "../Layout/FlexRow/FlexRow";
import { colorPalette } from "../../theme/colors";
import { Icon } from "../Icon/Icon";
import { useCallback, useEffect, useMemo, useState } from "react";

export const OTPInputField = ({ ...props }: OTPInputFieldProps) => {
  const {
    value,
    onChange,
    maxLength = 6,
    disabled,
    label,
    variant = "default",
    helpText,
    showPasswordToggle = false,
  } = props;

  const [selectedInput, setSelectedInput] = useState(1);
  const [showPassword, setShowPassword] = useState(true);
  const valueArray = useMemo(() => value.split(""), [value]);
  const counter = valueArray.length;

  const togglePasswordShown = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const getNextSelectedInput = useCallback(
    (valueLength: number, maxLength: number) => {
      return valueLength < maxLength ? valueLength + 1 : valueLength || 1;
    },
    []
  );

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const newValue = event.target.value;
      if (!/^\d$/.test(newValue)) return;
      if (
        (counter === maxLength && newValue !== "") ||
        disabled === true ||
        isNaN(Number(newValue)) ||
        newValue === " "
      )
        return;

      const newValueArray = [...valueArray];
      newValueArray[index] = newValue;
      const newValueString = newValueArray.join("");

      if (newValueString !== value) {
        onChange(newValueString);
        setSelectedInput(
          getNextSelectedInput(newValueString.length, maxLength)
        );
      }
    },
    [
      counter,
      disabled,
      maxLength,
      onChange,
      value,
      valueArray,
      getNextSelectedInput,
    ]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedData = event.clipboardData.getData("text").trim();

      if (/^\d+$/.test(pastedData)) {
        const validPastedData = pastedData.slice(0, maxLength);
        if (validPastedData !== value) {
          onChange(validPastedData);
          setSelectedInput(validPastedData.length);
        }
      }
    },
    [maxLength, onChange, value]
  );

  const handleInput = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement;
      const newValue = input.value;

      if (/^\d+$/.test(newValue)) {
        const validValue = newValue.slice(0, maxLength);
        if (validValue !== value) {
          onChange(validValue);
          setSelectedInput(validValue.length);
        }
      }
    },
    [maxLength, onChange, value]
  );

  const focusOnCorrectInput = useCallback(() => {
    let nextInput = selectedInput;
    if (selectedInput < valueArray.length) {
      nextInput = getNextSelectedInput(value.length, maxLength);
      setSelectedInput(nextInput);
    }
    const input = document.getElementById(`otp-input-${nextInput}`);
    if (input) {
      input.focus();
    }
  }, [
    selectedInput,
    valueArray.length,
    value.length,
    maxLength,
    getNextSelectedInput,
  ]);

  const handleOnFocus = useCallback(() => {
    focusOnCorrectInput();
  }, [focusOnCorrectInput]);

  const handleBackSpace = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace" && counter > 0) {
        const newValueArray = [...valueArray].splice(0, counter - 1);
        const newValue = newValueArray.join("");
        if (newValue !== value) {
          onChange(newValue);
          if (newValue.length < maxLength) {
            setSelectedInput(newValue.length + 1);
          } else {
            setSelectedInput(newValue.length);
          }
        }
      }
    },
    [counter, valueArray, value, onChange, maxLength]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
        e.preventDefault();
      }
    },
    []
  );

  useEffect(() => {
    focusOnCorrectInput();
  }, [selectedInput, focusOnCorrectInput]);

  const renderInputs = useMemo(() => {
    return Array(maxLength)
      .fill(1)
      .map((_, index) => (
        <OTPInputFieldBox
          key={index}
          onKeyUp={handleBackSpace}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onInput={handleInput}
          variant={variant}
          value={valueArray[index] || ""}
          type={showPassword ? "tel" : "password"}
          onFocus={(e) => {
            handleOnFocus();
            e.stopPropagation();
            e.preventDefault();
          }}
          id={`otp-input-${index + 1}`}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleOnChange(event, index)
          }
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus={index === 0}
          disabled={disabled}
        />
      ));
  }, [
    maxLength,
    handleBackSpace,
    handleKeyDown,
    handlePaste,
    handleInput,
    variant,
    valueArray,
    showPassword,
    handleOnFocus,
    handleOnChange,
    disabled,
  ]);

  return (
    <Wrapper>
      <OTPInputFieldContainer>
        <FlexColumn>
          <FlexRow alignItems="center" gap="small">
            <OTPInputFieldWrapper variant={variant} style={{ flexGrow: 1 }}>
              <Caps2SemiboldTypography color={colorPalette.black[100]}>
                {label}
              </Caps2SemiboldTypography>
              <OTPInputFieldsContainer>{renderInputs}</OTPInputFieldsContainer>
            </OTPInputFieldWrapper>
            {showPasswordToggle && (
              <FlexColumn width="auto">
                <Icon
                  onClick={togglePasswordShown}
                  name={
                    showPassword ? "fa-solid fa-eye" : "fa-solid fa-eye-slash"
                  }
                  color={colorPalette.black[200]}
                />
              </FlexColumn>
            )}
          </FlexRow>
          {helpText && (
            <SupportingText variant={variant}>
              <CircleIcon className="fa-solid fa-circle" variant={variant} />
              <Body4RegularTypography color={colorPalette.popBlack[200]}>
                {helpText}
              </Body4RegularTypography>
            </SupportingText>
          )}
        </FlexColumn>
      </OTPInputFieldContainer>
    </Wrapper>
  );
};
